import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
    formatTooltipData,
} from "./utils/filterChartData";

export function DebtToCashflows({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const transformedData = (data || []).map((item) => ({
        ...item,
        netDebtToEbitdaComputed:
            item.netDebtToEbitdaFromRemittanceReport != null
                ? item.netDebtToEbitdaFromRemittanceReport
                : item.netDebtToEbitda,
        netDebtToPreMarketingEbitdaComputed:
            item.netDebtToPreMarketingEbitdaFromRemittanceReport != null
                ? item.netDebtToPreMarketingEbitdaFromRemittanceReport
                : item.netDebtToPreMarketingEbitda,
        netDebtToRevenueComputed:
            item.netDebtToRevenueFromRemittanceReport != null
                ? item.netDebtToRevenueFromRemittanceReport
                : item.netDebtToRevenue,
    }));

    const filteredData = filterNullValuesFromChartData(transformedData);
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Debt to Cashflows"
            description="Exceptionally high and volatile net debt to EBITDA and net debt to revenue ratios, combined with frequent negative EBITDA, indicate an unsustainable leverage profile and elevated credit risk."
            isCondensed={isCondensed}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "netDebtToEbitdaComputed",
                        yName: "Net Debt to EBITDA",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.netDebtToEbitdaComputed;

                                return {
                                    heading: `${month}`,
                                    title: "Net Debt to EBITDA",
                                    data: formatTooltipData([
                                        {
                                            label: "Net Debt to EBITDA",
                                            value:
                                                ratioValue === Infinity
                                                    ? "∞"
                                                    : ratioValue === -Infinity
                                                      ? "-∞"
                                                      : `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "EBITDA",
                                            value: formatCurrency(datum.ebitda),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "netDebtToPreMarketingEbitdaComputed",
                        yName: "Net Debt to Pre-Marketing EBITDA",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.netDebtToPreMarketingEbitdaComputed;

                                return {
                                    heading: `${month}`,
                                    title: "Net Debt to Pre-Marketing EBITDA",
                                    data: formatTooltipData([
                                        {
                                            label: "Net Debt to Pre-Marketing EBITDA",
                                            value:
                                                ratioValue === Infinity
                                                    ? "∞"
                                                    : ratioValue === -Infinity
                                                      ? "-∞"
                                                      : `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Pre-Marketing EBITDA",
                                            value: formatCurrency(
                                                datum.preMarketingEbitda,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "netDebtToRevenueComputed",
                        yName: "Net Debt to Revenue",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.netDebtToRevenueComputed;

                                return {
                                    heading: `${month}`,
                                    title: "Net Debt to Revenue",
                                    data: formatTooltipData([
                                        {
                                            label: "Net Debt to Revenue",
                                            value:
                                                ratioValue === Infinity
                                                    ? "∞"
                                                    : ratioValue === -Infinity
                                                      ? "-∞"
                                                      : `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Total Revenue",
                                            value: formatCurrency(
                                                datum.totalRevenue,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        type: "number",
                        position: "right",
                        label: {
                            formatter: (params) => {
                                if (params.value === Infinity) return "∞";
                                if (params.value === -Infinity) return "-∞";
                                return `${params.value.toFixed(1)}x`;
                            },
                        },
                        keys: [
                            "netDebtToEbitdaComputed",
                            "netDebtToPreMarketingEbitdaComputed",
                            "netDebtToRevenueComputed",
                        ],
                    },
                ],
            }}
        />
    );
}
